import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, RotateCcw, Calculator, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import ParametrosNominaCard from './ParametrosNominaCard';
import {
  getPayrollConfigs,
  updatePayrollConfig,
  initializePayrollConfigs,
} from './Services/parametrizacion.services';

// Valores que fija el Código Sustantivo del Trabajo. Se muestran como
// referencia para que quien edita no termine, como ya pasó, con un festivo
// nocturno en 2.10 cuando la norma da 2.15.
const REFERENCIA_LEGAL = {
  horaBase: 'Valor base. Salario mensual dividido entre 240 horas.',
  horaExtraDiurna: 'Hora extra diurna: recargo del 25 % (art. 199 C.S.T.).',
  horaExtraNocturna: 'Hora extra nocturna: recargo del 75 % (art. 199 C.S.T.).',
  recargoNocturno: 'Recargo nocturno: 35 % sobre la hora ordinaria (art. 199 C.S.T.).',
  festivoDiurno: 'Trabajo en día festivo diurno: recargo del 80 % (art. 179 C.S.T.).',
  festivoNocturno: 'Trabajo en día festivo nocturno: recargo del 115 % (art. 179 C.S.T.).',
  dominicalDiurno: 'Trabajo dominical diurno: recargo dominical (art. 179 C.S.T.; validar el porcentaje vigente).',
  dominicalNocturno: 'Trabajo dominical nocturno: recargo dominical más nocturno (validar el porcentaje vigente).',
};

const RecargosPage = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [cambios, setCambios] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getPayrollConfigs();
      setItems(res?.data || []);
      setCambios({});
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  const cambiar = (id, valor) => {
    setCambios((prev) => ({ ...prev, [id]: valor }));
  };

  const guardarUno = async (item) => {
    const valor = cambios[item._id];
    if (valor === undefined) return;
    setGuardando(item._id);
    try {
      await updatePayrollConfig(item._id, { multiplicador: Number(valor) });
      toast({ title: 'Éxito', description: `${item.nombre} actualizado.` });
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const guardarTodos = async () => {
    const ids = Object.keys(cambios);
    if (!ids.length) return;
    setGuardando('todos');
    try {
      await Promise.all(
        ids.map((id) => updatePayrollConfig(id, { multiplicador: Number(cambios[id]) })),
      );
      toast({
        title: 'Éxito',
        description: `Se actualizaron ${ids.length} multiplicadores.`,
      });
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const restaurar = async () => {
    setGuardando('init');
    try {
      await initializePayrollConfigs();
      toast({
        title: 'Listo',
        description: 'Se cargaron los multiplicadores por defecto.',
      });
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setGuardando(null);
    }
  };

  const hayCambios = Object.keys(cambios).length > 0;
  // Bases creadas antes de los dominicales: "Cargar valores por defecto" agrega solo los que faltan.
  const faltanDominicales =
    !loading && items.length > 0 && !items.some((i) => i.tipo === 'dominicalDiurno');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        <ParametrosNominaCard />

        <details className="rounded-lg border bg-card">
          <summary className="cursor-pointer select-none p-4 text-sm font-medium">
            Multiplicadores de la liquidación individual antigua (/liquidacion-personal) — no afectan la nómina por
            periodo
          </summary>
          <div className="space-y-4 px-4 pb-4">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={restaurar} disabled={!!guardando}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Cargar valores por defecto
          </Button>
          <Button onClick={guardarTodos} disabled={!hayCambios || !!guardando}>
            <Save className="mr-2 h-4 w-4" />
            Guardar cambios
          </Button>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">
            Estos multiplicadores solo los usa la liquidación individual antigua
            (/liquidacion-personal). Los de la nómina por periodo se cambian arriba, en «Parámetros de
            nómina» → «Porcentajes que aplica la nómina».
          </p>
        </div>

        {faltanDominicales && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <span>
              Faltan los multiplicadores dominicales de la liquidación individual.
            </span>
            <Button size="sm" variant="outline" onClick={restaurar} disabled={!!guardando}>
              Agregar los que faltan
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="rounded-lg border p-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Todavía no hay multiplicadores configurados.
            </p>
            <Button onClick={restaurar} disabled={!!guardando}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Cargar valores por defecto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((item) => {
              const valorActual =
                cambios[item._id] !== undefined ? cambios[item._id] : item.multiplicador;
              const modificado = cambios[item._id] !== undefined;
              return (
                <div
                  key={item._id}
                  className={`rounded-lg border bg-card p-4 space-y-3 ${
                    modificado ? 'border-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{item.nombre}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {item.tipo}
                      </p>
                    </div>
                    <Badge variant={item.activo ? 'default' : 'outline'}>
                      {item.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input
                      type="number"
                      step="0.01"
                      min="1"
                      value={valorActual ?? ''}
                      onChange={(e) => cambiar(item._id, e.target.value)}
                      className="h-9"
                    />
                    {modificado && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => guardarUno(item)}
                        disabled={guardando === item._id}
                      >
                        <Save className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {item.descripcion || REFERENCIA_LEGAL[item.tipo] || ''}
                  </p>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </details>
      </div>
    </motion.div>
  );
};

export default RecargosPage;